/**
 * Раскрытие строки «Голоса клиента» до разговоров: по одной формулировке с
 * каждого, свежие первыми, переход в разговор, «показаны последние X из Y».
 * Одно на строку раздела (`VoiceRowCard`) и на клетку разбивки отказов
 * (`DeclineBreakdownPanel`, #1641) — управляет им тот, кто его открыл.
 *
 * Строка темы (#845) начинается с её определения, категорий, качества, отказов и
 * вопросов без ответа (`VoiceThemeDetails`); у разговоров темы формулировки нет —
 * вместо неё категория разговора.
 */
import { useTranslation } from 'react-i18next';
import { VoiceObjectionRowDetails } from '../objections/VoiceObjectionRowDetails';
import { VoiceBucketHeader } from './VoiceBucketHeader';
import { VoiceItemList } from './VoiceItemList';
import { VoiceRowTrend } from './VoiceRowTrend';
import { VoiceThemeDetails } from './VoiceThemeDetails';
import { bucketNote, pickedItems, useTrendBucket } from './useTrendBucket';
import type { VoiceRowDetailsTrend } from './voice-series-view-model';
import { isSmallSample, isThemeKind, type VoiceRow } from './voice-view-model';

interface VoiceRowDetailsProps {
  id: string;
  row: Pick<VoiceRow, 'kind' | 'calls' | 'items' | 'competitor' | 'theme' | 'objection'>;
  onOpenCall: (recordId: string) => void;
  /**
   * What an empty wording says. By default — «wording hidden» (the redaction
   * removed it); a string — that text; `null` — nothing: the conversation
   * may have had no wording to remove (a decline without a named reason).
   */
  emptyWording?: string | null;
  /** The row's series: a chart whose bucket narrows the conversations below. */
  trend?: VoiceRowDetailsTrend;
}

export function VoiceRowDetails({
  id,
  row,
  onOpenCall,
  emptyWording,
  trend,
}: VoiceRowDetailsProps) {
  const { t } = useTranslation('dashboard');
  const themed = isThemeKind(row.kind);
  // A bucket of the period: only its conversations, out of the latest ones the
  // row carries — how many of the bucket's K they are is said below.
  const pick = useTrendBucket(trend);
  // An objection row opens like its code in the Objections section (#2552):
  // its quotes are a sample, not «the latest N of M conversations».
  if (row.objection) {
    return <VoiceObjectionRowDetails id={id} code={row.objection} trend={trend} />;
  }
  const { bucket } = pick;
  const items = pickedItems(pick, row.items);
  const listNote =
    bucket &&
    pick.calls !== 0 &&
    items.length === 0 &&
    themed &&
    row.items.length === 0 &&
    row.calls > 0
      ? // The reader's grant withholds the theme's conversations
        // (`themesWithoutCalls`): not an older list, an access.
        t('voicePage.themes.analytics.callsWithheld')
      : bucketNote(pick, items.length, t);
  return (
    <div id={id} className="space-y-3 border-t border-border p-3">
      {trend && (
        <VoiceRowTrend
          seriesRef={trend.seriesRef}
          calls={row.calls}
          series={trend.series}
          unit={trend.unit}
          selected={pick.index}
          onSelect={pick.select}
          monthPatch={trend.monthPatch}
        />
      )}
      {isSmallSample(row.calls) && (
        <p className="text-xs text-fg-subtle">{t('voicePage.smallSample')}</p>
      )}
      {row.competitor && row.calls > 0 && (
        <p className="text-xs text-fg-muted">
          {t('voicePage.competitors.sentiment', row.competitor.sentiment)}
        </p>
      )}
      {row.theme && (
        <VoiceThemeDetails theme={row.theme} calls={row.calls} onOpenCall={onOpenCall} />
      )}
      {bucket ? (
        <div className="space-y-2">
          <VoiceBucketHeader period={pick.text} onClear={pick.clear} />
          {items.length > 0 && (
            <VoiceItemList
              row={row}
              items={items}
              onOpenCall={onOpenCall}
              emptyWording={emptyWording === undefined && themed ? null : emptyWording}
            />
          )}
          {listNote && <p className="text-xs text-fg-subtle">{listNote}</p>}
        </div>
      ) : row.items.length === 0 ? (
        <p className="text-xs text-fg-subtle">
          {themed
            ? // A counted theme row without conversations is a reader whose
              // grant withholds them (`themesWithoutCalls`), not an empty row.
              row.calls > 0
              ? t('voicePage.themes.analytics.callsWithheld')
              : t('voicePage.themes.analytics.noCalls')
            : row.calls > 0
              ? t('voicePage.noWordings')
              : t('voicePage.noCalls')}
        </p>
      ) : (
        <VoiceItemList
          row={row}
          items={row.items}
          onOpenCall={onOpenCall}
          emptyWording={emptyWording === undefined && themed ? null : emptyWording}
        />
      )}
      {!bucket && row.items.length > 0 && row.calls > row.items.length && (
        <p className="text-xs text-fg-subtle">
          {t('voicePage.shownOf', { shown: row.items.length, count: row.calls })}
        </p>
      )}
    </div>
  );
}
